import React from "react";
import { useRouter } from "next/router";
import styled, { createGlobalStyle } from "styled-components";
import { ALink, FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import { Typography as DefaultTypography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import { space } from "styled-system";
import useDisableIntercom from "@/hooks/useDisableIntercom";

export const TermsPageContent = (props) => {
  const { showHeader = false, webmode = false } = props || {};

  const location = useRouter();
  const isFromAppSource = location?.query;

  const showTopSpace = isFromAppSource?.source !== "app";

  return (
    <Container flexDirection={"column"}>
      {showHeader && (
        <Typography
          fontFamily={"var(--font-500)"}
          color={theme.colors.gray_100}
          variant="heading1"
        >
          Terms of Service
        </Typography>
      )}
      <Wrapper flexDirection={"column"} showHeightGap={showTopSpace}>
        <FlexDiv style={{ gap: "10px" }}>
          <Typography
            fontFamily={"var(--man-600)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
            style={{
              fontStyle: "italic",
              margin: "0px",
            }}
          >
            Last updated:
          </Typography>
          <Typography
            fontFamily={"var(--man-500)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
          >
            February 06, 2024
          </Typography>
        </FlexDiv>
        <FlexDiv style={{ gap: "10px", marginBottom: "20px" }}>
          <Typography
            fontFamily={"var(--man-600)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
            style={{
              fontStyle: "italic",
            }}
          >
            Effective From:
          </Typography>
          <Typography
            fontFamily={"var(--man-500)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
          >
            February 06, 2024
          </Typography>
        </FlexDiv>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--man-400)"}
          color={theme.colors.gray_100}
        >
          This Terms of Service agreement (<Strong>“Agreement”</Strong>) governs
          your (<Strong>“You”</Strong>, or <Strong>“Your”</Strong>) use of
          services made available by Blacktail Mindhouse Private Limited. (“
          <Strong>Mindhouse”</Strong>, <Strong>“We”</Strong>,{" "}
          <Strong>“Us”</Strong>, or <Strong>“Our”</Strong>), websites at the URL{" "}
          <Typography
            variant="link1"
            link1Color={theme.colorsV2.blue.shade_700}
            href="https://www.betheshyft.com/"
            fontFamily={"var(--man-400)"}
          >
            https://www.betheshyft.com/
          </Typography>
          ,{" "}
          <Typography
            variant="link1"
            link1Color={theme.colorsV2.blue.shade_700}
            href="https://www.mindhouse.com/"
            fontFamily={"var(--man-400)"}
          >
            https://www.mindhouse.com/
          </Typography>
          ,{" "}
          <Typography
            variant="link1"
            link1Color={theme.colorsV2.blue.shade_700}
            fontFamily={"var(--man-400)"}
            href="https://www.dash.health/"
          >
            https://www.dash.health/
          </Typography>{" "}
          and any other service, product, app, or website provided by Mindhouse
          that explicitly specified the applicability of Agreement
          (collectively, <Strong>“Platform”</Strong>) including, but not limited
          to, free trials, paid services, enterprise services and/or any other
          service made available by Mindhouse that indicate the applicability of
          this Agreement. Mindhouse is entering into this Agreement on behalf of
          itself and its Affiliates (as defined below). Please carefully read
          the contents of this Agreement before using the Platform or the
          Services (as defined below).
        </Typography>

        <Typography
          variant="paragraph2"
          fontFamily={"var(--man-400)"}
          color={theme.colors.gray_100}
        >
          By undertaking any of the following actions, You demonstrate that You
          have read and agree to this Agreement: (1) by signing up for a free
          trail, (2) by signing up for a paid service, (3) by using the Platform
          or Service (as defined below), or (4) by ticking a box indicating
          acceptance of this Agreement, or (5) by creating an account on the
          Platform. You further indicate that You have read, understood, and
          agree to the following: (1) Privacy Policy, (2) Cookie Policy and (3)
          EULA (End User License Agreement). The latest version of these
          documents can be accessed at:
        </Typography>
        <ul
          style={{
            marginLeft: "20px",
          }}
        >
          <li>
            Privacy Policy:{" "}
            <Typography
              variant="link1"
              link1Color={theme.colorsV2.blue.shade_700}
              fontFamily={"var(--man-400)"}
              href="https://www.betheshyft.com/privacy-policy/"
            >
              https://www.betheshyft.com/privacy-policy/
            </Typography>
          </li>
          <li>
            Cookie Policy:{" "}
            <Typography
              variant="link1"
              fontFamily={"var(--man-400)"}
              link1Color={theme.colorsV2.blue.shade_700}
              href="https://www.betheshyft.com/cookie-policy/"
            >
              https://www.betheshyft.com/cookie-policy/
            </Typography>
          </li>
          <li>
            EULA:{" "}
            <Typography
              variant="link1"
              fontFamily={"var(--man-400)"}
              link1Color={theme.colorsV2.blue.shade_700}
              href="https://www.betheshyft.com/end-user-licence-agreement/"
            >
              https://www.betheshyft.com/end-user-licence-agreement/
            </Typography>
          </li>
        </ul>

        <Typography
          variant="paragraph2"
          fontFamily={"var(--man-400)"}
          color={theme.colors.gray_100}
        >
          If the individual accepting this Agreement is accepting on behalf of a
          company or other legal entity, such individual represents that they
          have the authority to bind such entity to these terms and conditions.
          If the individual accepting this Agreement does not have such
          authority or does not agree with this Agreement, such individual must
          not accept this Agreement and may not use the Services.
        </Typography>

        <Typography
          variant="paragraph2"
          fontFamily={"var(--man-400)"}
          color={theme.colors.gray_100}
        >
          If You are an individual below the legal age of majority as per the
          Applicable Law (<Strong>“Minor”</Strong>), Your parent or legal
          guardian must read and accept this Agreement before You can use the
          Platform. By permitting a Minor to use the Services, a Minor’s parent
          or legal guardian agrees to agree to the terms and conditions of this
          Agreement, and further agrees to be responsible for all acts or
          omissions of such Minor on the Platform.
        </Typography>

        <Typography
          variant="paragraph2"
          fontFamily={"var(--man-400)"}
          color={theme.colors.gray_100}
        >
          This Agreement is effective between You and Mindhouse as of the date
          on which You accept the Agreement.
        </Typography>

        <OL pl={"0px"}>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Definitions.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Affiliates”</Strong> means any entity that directly
                  or indirectly controls, is controlled by, or is under common
                  control with the subject entity. “Control,” for purposes of
                  this definition, means direct or indirect ownership or control
                  of more than 50% of the voting interests of the subject
                  entity.
                </Typography>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>“Agreement”</Strong> means this Terms of Service
                    agreement and includes the Privacy Policy, Cookie Policy,
                    and any other additional terms and conditions as may be
                    agreed upon by the parties in writing prior to the provision
                    of additional services by Mindhouse.
                  </Typography>
                </LI>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>“Applicable Law”</Strong> means any law, rules,
                    regulations, ordinances, orders, directives, codes,
                    judgements, decrees, licenses, authorizations, and
                    directives, as applicable from time to time.
                  </Typography>
                </LI>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>“Content”</Strong> means audio, video, text,
                    interactive content, and other content.
                  </Typography>
                </LI>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>“Confidential Information”</Strong> means and
                    includes all non-public and/or private information disclosed
                    by the Disclosing Party to the Receiving Party, whether
                    orally or in writing, that is designated as confidential or
                    that reasonably should be understood to be confidential
                    given the nature of the information and the circumstances of
                    disclosure. Confidential Information of Customer includes
                    Customer Data. Confidential Information of Mindhouse
                    includes non-public aspects of the software, Platform
                    Content, documentation related to the Platform or Services,
                    and Mindhouse’s technical know-how, materials, product
                    development plans, marketing plans, and business
                    information. Notwithstanding anything to the contrary above,
                    Confidential Information does not include any information
                    that (i) is or becomes generally known to the public without
                    breach of any obligation owed to the Disclosing Party, (ii)
                    was known to the Receiving Party prior to its disclosure by
                    the Disclosing Party without breach of any obligation owed
                    to the Disclosing Party, (iii) is received from a third
                    Party without breach of any obligation owed to the
                    Disclosing Party, or (iv) is independently developed without
                    access to or use of any Confidential Information.
                  </Typography>
                </LI>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Customer”</Strong> means: (i) in the case of an
                  individual accepting this Agreement on his or her own behalf,
                  such individual; (ii) in the case of an individual accepting
                  this Agreement on behalf of a company or other legal entity,
                  the company or other legal entity on behalf of which such
                  individual is accepting this Agreement; or (iii) in the case
                  of legal guardian or parent accepting this Agreement on behalf
                  of a Minor, such legal guardian or parent.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Customer Content”</Strong> means Content provided by
                  the Customer to its Users on or through the Platform.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Customer Data”</Strong> means all electronics data or
                  information submitted by Customer or its Users and stored by
                  Mindhouse for Customer.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Disclosing Party”</Strong> means the party that
                  discloses Confidential Information to the other Party.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Force Majeure Event”</Strong> means an event or
                  effect that cannot be reasonably anticipated or controlled,
                  which even by the exercise of reasonable diligence cannot
                  prevent non-performance, and in particular, performance of the
                  Services. These occurrences and events include, but are not
                  limited to: acts of God; acts of government; acts of war; acts
                  of public enemies; acts of terror; strikes or other labor
                  problems; natural disaster; floods; fires; earthquakes;
                  epidemic; pandemic; civil unrest; riots; explosions; outages;
                  general Internet brown-outs or black-outs or shortage of
                  bandwidth; hardware failures; hacking, denial of service or
                  ransomware attacks; actions of the elements; or other similar
                  causes beyond the control of Customer or Mindhouse in the
                  performance of this Agreement.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Free Service(s)”</Strong> means Services that
                  Mindhouse makes available to Customers at no cost, including
                  but not limited to services provided for evaluation purposes
                  without a fee.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Malicious Code”</Strong> means code, files, scripts,
                  agents, or programs intended to cause damage or harm,
                  including but not limited to, viruses, worms, time bombs, and
                  Trojan horses.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Platform Content”</Strong> means Content, provided by
                  Mindhouse to Customer or Customer’s Users, on or through the
                  Platform, but not including Vendor Content, Customer Content,
                  or User Content.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Minor”</Strong> has the meaning assigned to it above.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Platform”</Strong> means{" "}
                  <Typography
                    variant="link1"
                    link1Color={theme.colorsV2.blue.shade_700}
                    fontFamily={"var(--man-400)"}
                    href="https://www.betheshyft.com/"
                  >
                    https://www.betheshyft.com/
                  </Typography>
                  ,
                  <Typography
                    variant="link1"
                    link1Color={theme.colorsV2.blue.shade_700}
                    fontFamily={"var(--man-400)"}
                    href="https://www.mindhouse.com/"
                  >
                    https://www.mindhouse.com/
                  </Typography>
                  ,
                  <Typography
                    variant="link1"
                    link1Color={theme.colorsV2.blue.shade_700}
                    fontFamily={"var(--man-400)"}
                    href="https://www.dash.health/"
                  >
                    https://www.dash.health/
                  </Typography>
                  , and any other service, product, app, or website provided by
                  Mindhouse that explicitly specifies the applicability of this
                  Agreement.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Purchased Services”</Strong> means Services that
                  Customer purchases for a fee, as distinguished from Free
                  Services.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Receiving Party”</Strong> means the party that
                  received Confidential Information from the other party.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Service”</Strong> means all products, services,
                  subscriptions, Mindhouse Content, and Vendor Content, that are
                  availed by Customer or its Users, or provided to Customer or
                  Customer’s Users by Mindhouse.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong> “User”</Strong> means any individual or entity
                  authorized by the Customer implicitly or explicitly to use the
                  Service. In case the Customer is an individual using the
                  Services on its own behalf, User means such Customer.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“User Content”</Strong> means Content provided by
                  Users on or through the Platform.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“Vendor”</Strong> means third party(ies) contracted by
                  Mindhouse to provide services on the Platform, including, but
                  not limited to, trainer(s), nutritionist(s), and therapist(s).
                  “Vendor Content” means Content provided by Vendor to Customer
                  or Users on or through the Platform, or in the course of
                  providing its services to the Customer or Users.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>“You”</Strong> or <Strong>“Your”</Strong> means
                  Customer, Minor and/or User, as may be applicable depending
                  upon the context in which it is used.
                </Typography>
              </LI>
            </OL>
          </LI>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Mindhouse’s Responsibilities.
            </Heading>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Provision of Purchased Services.</Strong> Mindhouse
                  will:
                </Typography>
                <OL mb={"25px"}>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      make the Services available to the Customer pursuant to
                      this Agreement;
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      use commercially reasonable efforts to make the Purchased
                      Services available except for (i) planned or unplanned
                      downtime, and (ii) any Force Majeure Event under the terms
                      of this Agreement; and
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      provide the Services in accordance with Applicable Laws
                      and government regulations, and subject to use of the
                      Services in accordance with this Agreement.
                    </Typography>
                  </LI>
                </OL>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Customer Data.</Strong> Customer understands and
                  agrees that Mindhouse collects certain data and information
                  about the Customer and Users as described in the Privacy
                  Policy. Customer agrees to the collection of such data on its
                  behalf and on behalf of its User. The Customer further
                  understands and agrees that in the event that a User does not
                  agree to provide such data, the User may not use the Services.
                  Mindhouse will not be liable or responsible for any failure to
                  provide Services resulting from User’s refusal to provide any
                  data or information as described in the Privacy Policy.
                  Mindhouse will maintain appropriate administrative, physical,
                  and technical safeguards for the protection of the security,
                  confidentiality, and integrity of Customer Data, as described
                  in detail in Our Privacy Policy. Those safeguards include, but
                  are not limited to, measures designed to prevent unauthorized
                  access to or disclosure of Customer Data (other than by
                  Customer or Users themselves). For more details on how
                  Mindhouse protects Customer Data, please refer to Mindhouse’s
                  Privacy Policy.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Mindhouse’s Personnel.</Strong> Mindhouse will be
                  responsible for the performance of its personnel (including
                  its employees and contractors) and their compliance with
                  Mindhouse’s obligations under this Agreement, except as
                  otherwise specified explicitly in this Agreement.
                </Typography>
              </LI>
            </OL>
          </LI>
          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Subscription terms
            </Heading>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Subscription Plans.</Strong> Customer understands and
                  agrees that the Services provided by Mindhouse are provided
                  through subscription fee-based programs available on the
                  Platform, as updated from time to time. On request, Mindhouse
                  may also provide an option to undertake a single consultation.
                </Typography>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Order Placement and Confirmation.</Strong> To avail
                    the Services, the Customer must purchase a subscription plan
                    through the Platform (<Strong>“Order”</Strong>). Customer
                    understands and agrees that the Order is an offer to
                    Mindhouse to obtain the Services. Placing the Order, or
                    paying for the Order, does not guarantee that the Services
                    would be provided. Order confirmation is subject to
                    acceptance by Mindhouse, at its sole discretion. Order
                    confirmations will be provided by Mindhouse by way of
                    sending an email to Customer’s registered email address.
                  </Typography>
                </LI>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Right to Cancel Order.</Strong> Customer understands
                    and agrees that Mindhouse may, at its sole discretion,
                    cancel or not process Order(s). In the event that Mindhouse
                    does not accept an Order, Mindhouse will return the
                    subscription fees received by Mindhouse.
                  </Typography>
                </LI>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  fontStyle={"italic"}
                  color={theme.colors.gray_100}
                >
                  <Strong>Right to Verify.</Strong> You understand and agree
                  that Mindhouse may, at its sole discretion, request You to
                  furnish proof of identity prior to providing confirmation to
                  Order to verify Your identity.
                </Typography>
              </LI>
            </OL>
          </LI>
          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Use of Service.
            </Heading>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Membership of Minor.</Strong> In the event that You
                  are a Minor, Your parent or legal guardian must consent to
                  this Agreement prior to Your use of the Services. We may, at
                  Our discretion, require the parent or legal guardian to
                  furnish proof of identity and/or proof of authorization to
                  provide such consent to Mindhouse. The parent or legal
                  guardian (as the case may be) of such Minor agrees to
                  indemnify, defend and hold harmless Mindhouse and its
                  Affiliates from and against any and all losses, liabilities,
                  claims, damages, costs and expenses (including legal fees and
                  disbursements in connection therewith and interest chargeable
                  thereon) asserted against or incurred by Mindhouse and/or its
                  Affiliates that arise out of, result from, or may be payable
                  by virtue of, any breach or non-performance of any
                  representation, warranty, covenant or agreement made or
                  obligation to be performed by the Minor pursuant to this
                  Agreement. Parents or legal guardians of the Minor may
                  exercise the rights specified under Privacy Policy on behalf
                  of a Minor.
                </Typography>
                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Prior Consultation Requirement.</Strong> In the
                    event You (a) are expecting/suspecting to be pregnant, or
                    (b) wear a pacemaker, or (c) suffer from epilepsy or
                    seizures, or (d) have a history of mental illness, or (e)
                    have eating disorder, or (f) have diabetes, or (g) have any
                    other physical/ psychological/ emotional/ medical condition,
                    or (h) are taking any strong medication, or (i) are above
                    the age of 65 years, You must consult a medical health care
                    provider prior to use of Services, and prior to following
                    any advice provided by Mindhouse or a Vendor.
                  </Typography>
                </LI>

                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Usage Limits.</Strong> Mindhouse may place technical
                    or non-technical limitations on the use of the Service(s) by
                    Customer and/or User, such as based on the plan or
                    subscription or services purchased by a Customer or to
                    protect the Platform or to ensure fair usage (“Usage
                    Limits”). Mindhouse shall not be liable for any loss or
                    damage faced by You or any third party as a result of any
                    Usage Limits. Mindhouse may, where technically possible and
                    feasible, offer to increase certain Usage Limits subject to
                    payment of an additional fee. Customer understands that
                    Mindhouse may refuse to increase a Usage Limit for any
                    reason or no reason at its sole discretion.
                  </Typography>
                </LI>

                <LI>
                  <Typography
                    fontFamily={"var(--man-400)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Your Responsibilities.</Strong>
                  </Typography>
                  <OL>
                    <LI>
                      <Typography
                        fontFamily={"var(--man-400)"}
                        variant="paragraph2"
                        color={theme.colors.gray_100}
                      >
                        You understand and agree that:
                      </Typography>

                      <OL>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You shall be solely responsible and liable for Your
                            conduct and breach of this Agreement by You, and if
                            You are a company or any other entity You are also
                            responsible and liable for the conduct of Your Users
                            and breach of this Agreement by Your Users;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You shall use commercially reasonable efforts to
                            prevent unauthorized access to or use of Services,
                            and promptly, but in no case later than forty-eight
                            (48) hours of gaining such knowledge, notify
                            Mindhouse of any such unauthorized access or use;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You shall be solely liable and responsible for
                            ensuring that Your account is not used by or on
                            behalf of any third-party;
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            if You are a Customer, You shall be solely liable
                            and responsible for Your handling of Your Users’
                            data;
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            if You are a Customer, You shall be solely
                            responsible for compliance with any state or federal
                            laws applicable to use of the Service and the
                            Platform by You and/or Your Users;
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You will ensure that the details specified in the
                            Order are accurate. In an event where the details
                            are incorrect, it is Your sole responsibility to
                            notify Mindhouse;
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You will provide true, accurate, and current
                            information as requested by Mindhouse at any time,
                            and promptly and regularly update Your information
                            to maintain its accuracy and completeness. Mindhouse
                            reserves the right to suspend or terminate Your
                            account and refuse any and all current or future use
                            of the Service, in whole or in part, by You for
                            providing any information that is untrue,
                            inaccurate, incomplete or not current;
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            Certain Services may be provided by Vendor(s). Under
                            no circumstances will Mindhouse be liable or
                            responsible for any action or inaction of any Vendor
                            (including but not limited to cancellations, advice
                            provided, breach of Applicable Law and/or this
                            Agreement, or any other activity). All disputes
                            between You and the Vendor must be resolved between
                            You and such Vendor only; and
                          </Typography>
                        </LI>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            the Services include certain inherent risks that
                            cannot be eliminated from the Services. You agree
                            and acknowledge to undertake the risks and hereby
                            release Mindhouse from any and all liabilities
                            arising out of such risks.
                          </Typography>
                        </LI>
                      </OL>
                    </LI>

                    <LI>
                      <Typography
                        fontFamily={"var(--man-400)"}
                        variant="paragraph2"
                        color={theme.colors.gray_100}
                      >
                        You further understand and agree that:
                      </Typography>

                      <OL>
                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You may book a slot for the Service in advance as
                            per the time period specified by Mindhouse on the
                            Platform. You agree and acknowledge that Mindhouse
                            may, at its sole discretion, without prior
                            notification, modify the time, date or duration of
                            Your slot and/or the available slots;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            You may only cancel or reschedule an appointment up
                            to a certain time limit, as specified by Mindhouse,
                            before the scheduled appointment. On expiration of
                            such time limit, You may not cancel or reschedule
                            the appointment. In such an event, You may contact
                            Us and Mindhouse may, but has no obligation to,
                            consider such requests;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            Mindhouse may cancel or reschedule any appointments
                            booked by User, and Mindhouse shall not be liable
                            for any loss, damage caused to User due to such
                            cancellation or reschedule;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            Mindhouse reserves a right to charge a cancellation
                            fee for cancellation of appointments;
                          </Typography>
                        </LI>

                        <LI>
                          <Typography
                            fontFamily={"var(--man-400)"}
                            variant="paragraph2"
                            color={theme.colors.gray_100}
                          >
                            Service(s) may contain features designed to
                            interoperate with non-Mindhouse applications.
                            Mindhouse cannot guarantee the continued
                            availability of such features, and may cease
                            providing them without notice, without assigning any
                            reason and without entitling Customer to any refund,
                            credit, or other compensation. We may, for example
                            and without limitation, cease offering such a
                            feature if the provider of a non-Mindhouse
                            application ceases to make the non-Mindhouse
                            application available for interoperation with the
                            corresponding features in a manner acceptable to
                            Mindhouse; and
                          </Typography>
                        </LI>
                      </OL>
                    </LI>

                    <LI>
                      <Typography
                        fontFamily={"var(--man-400)"}
                        variant="paragraph2"
                        color={theme.colors.gray_100}
                      >
                        Any use of the Services in breach of the foregoing by
                        You that in Mindhouse’s judgment threatens the security,
                        integrity, or availability of Mindhouse’s services, may
                        result in Mindhouse’s immediate suspension of the
                        Services; however, Mindhouse will use commercially
                        reasonable efforts under the circumstances to provide
                        You with notice and an opportunity to remedy such
                        violation or threat prior to any such suspension.
                      </Typography>
                    </LI>
                  </OL>
                </LI>
              </LI>
            </OL>
          </LI>
          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Usage Restrictions.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong> Platform and Services Restrictions.</Strong> You
                  shall not:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      share an account with any third party, share login
                      credentials of an account with any third party, or use any
                      third party’s account to access the Service;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      misrepresent the age to access Services intended for users
                      who are of or above legal age of majority;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      use the Services under the influence of heavy
                      medication(s) (without prior consultation), drugs,
                      alcohol, or any other inhibitors or prohibited substances;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      use a Service to store or transmit Malicious Code;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      interfere with or disrupt the integrity or performance of
                      any Service or third-party data contained therein;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      misuse Services by interfering with their normal operation
                      or attempting to access them using a method other than
                      through the interfaces and instructions provided by
                      Mindhouse;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      attempt to gain unauthorized access to any Service or
                      Platform or its related systems or networks;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      attempt, permit, or undertake direct or indirect access to
                      or use of any Services in a way that circumvents a
                      contractual usage limit, or use any Services to access or
                      use any of Mindhouse’s or Vendor’s intellectual property
                      except as permitted under this Agreement;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      except to the extent permitted by Applicable Law,
                      disassemble, reverse engineer, or decompile a Service or
                      Platform or access it to (i) build a competitive product
                      or service, (ii) build a product or service using similar
                      ideas, features, functions or graphics of the Service or
                      Platform, (iii) copy any ideas, features, functions or
                      graphics of the Service or Platform, or (iv) determine
                      whether the Services or Platform are within the scope of
                      any patent; and
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      engage in abusive or excessive usage of the Services or
                      Platform, which is usage significantly in excess of
                      average usage patterns that adversely affects the speed,
                      responsiveness, stability, availability, or functionality
                      of the Services for other users. Mindhouse reserves the
                      right to notify You of Your abusive or excessive usage to
                      provide You with an opportunity to reduce such usage to a
                      level acceptable to Mindhouse.
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      is patently false and untrue, and is written or published
                      in any form, with the intent to mislead or harass a
                      person, entity or agency for financial gain or to cause
                      any injury to any person.
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Legal Restrictions.</Strong> You shall not:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      host, display, upload, modify, publish, transmit, store,
                      update, or share any information that:
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      belongs to another person and to which You does not have
                      any right;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      is defamatory, obscene, pornographic, paedophilic,
                      invasive of another’s privacy, including bodily privacy,
                      insulting or harassing on the basis of gender, libellous,
                      racially or ethnically objectionable, relating or
                      encouraging money laundering or gambing, or otherwise
                      inconsistent with or contrary to the laws in force;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      is harmful to any child;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      infringes any patent, trademark, copyright or other
                      proprietary rights;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      violates any law for the time in force;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      deceives or misleads the addressee about the origin of the
                      message or knowingly and intentionally communicates any
                      information which is patently false or misleading in
                      nature but may reasonably be perceived as a fact;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      impersonates another person;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      threatens the unity, integrity, defence, security or
                      sovereignty of India, friendly relations with foreign
                      States, or public order, or causes incitement to the
                      commission of any cognizable offence or prevents
                      investigation of any offence or is insulting other nation;
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      contains software virus or any other computer code, file
                      or program designated to interrupt, destroy or limit the
                      functionality of any computer resource; and/or
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      is patently false and untrue, and is written or published
                      in any form, with the intent to mislead or harass a
                      person, entity or agency for financial gain or to cause
                      any injury to any person.
                    </Typography>
                  </LI>
                </OL>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Modifications to the Service.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Changes to the Services.</Strong> Mindhouse may add,
                  alter, or remove features from any Service, at any time at its
                  sole discretion. Mindhouse may limit, suspend or discontinue a
                  Service at any time in its sole discretion. Provided, however,
                  that no such addition, alteration, removal, limitation,
                  suspension, or discontinuation, will result in an overall
                  decrease in the functionality available to, and used by,
                  Customer during the then ongoing term of this Agreement.
                  Notwithstanding anything above, Mindhouse may undertake or
                  implement any such alteration, removal, limitation,
                  suspension, or discontinuation, if required for compliance
                  with the Applicable Law, law enforcement, court order,
                  third-party intellectual property rights, or other legal
                  obligation. Where possible and feasible, Mindhouse will
                  provide reasonable notice of not less than two weeks for any
                  such suspension or discontinuation.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>New Services.</Strong> Mindhouse may introduce new
                  products and services to complement the existing Services. If
                  such new services are not included in Your existing Purchased
                  Services, Mindhouse reserves the right and sole discretion to
                  decide whether or not the new services will be made available
                  to Customer, whether or not such new services will be made
                  available as part of Customer’s existing subscription, and
                  whether or not the availability of such new services shall be
                  subject to payment of an additional charge. Customer may, at
                  Customer’s sole discretion, choose to provide comments or
                  feedback to Mindhouse. Mindhouse may, but shall have no
                  obligation to, use the comments or feedback to improve
                  Service(s) offered by Mindhouse.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Free Services.</Strong> Mindhouse may provide Free
                  Services to Customer, including but not limited to free trials
                  of certain Services for the purpose of evaluation, subject to
                  the terms and conditions of this Agreement.
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      Free Services are provided without charge up to certain
                      limits, and usage beyond those limits will require
                      Customer to purchase additional resources or Services.
                      Customer acknowledges that the provision of Free Services
                      is subject to the limits placed upon them by Mindhouse and
                      agrees not to attempt to circumvent any such limits.
                      Customer should review the applicable Service's
                      documentation during the trial period to become familiar
                      with the features and functions of the Service before
                      making a purchase. Mindhouse may terminate Customer's
                      access to Free Services or any part thereof at any time
                      without prior notice and without liability to Customer or
                      any third party, including but not limited to Customer’s
                      Users.
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      NOTWITHSTANDING ANY OTHER PROVISIONS OF THIS AGREEMENT,
                      FREE SERVICES ARE PROVIDED “AS IS” WITHOUT ANY WARRANTY,
                      EXPRESS OR IMPLIED, AND MINDHOUSE DISCLAIMS ALL
                      WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                      MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                      NONINFRINGEMENT OF THIRD-PARTY RIGHTS. CUSTOMER AGREES
                      THAT MINDHOUSE SHALL HAVE NO INDEMNIFICATION OBLIGATIONS
                      NOR LIABILITY OF ANY KIND WITH RESPECT TO THE FREE
                      SERVICES, INCLUDING BUT NOT LIMITED TO ANY DAMAGES ARISING
                      OUT OF CUSTOMER’S USE OF THE FREE SERVICES, ANY BREACH BY
                      CUSTOMER OF THIS AGREEMENT, AND ANY OF CUSTOMER’S
                      INDEMNIFICATION OBLIGATIONS HEREUNDER. WITHOUT LIMITING
                      THE FOREGOING, MINDHOUSE AND ITS AFFILIATES AND LICENSORS
                      DO NOT REPRESENT OR WARRANT TO CUSTOMER THAT: (A)
                      CUSTOMER’S USE OF THE FREE SERVICES WILL MEET CUSTOMER’S
                      REQUIREMENTS, (B) CUSTOMER’S USE OF THE FREE SERVICES WILL
                      BE UNINTERRUPTED, TIMELY, SECURE, OR FREE FROM ERROR, OR
                      (C) DATA OR INFORMATION PROVIDED BY MINDHOUSE WILL BE
                      ACCURATE. NOTWITHSTANDING ANYTHING TO THE CONTRARY IN
                      SECTION 13, THE CUSTOMER SHALL BE FULLY LIABLE UNDER THIS
                      AGREEMENT TO MINDHOUSE AND ITS AFFILIATES FOR ANY DAMAGES
                      ARISING OUT OF THE CUSTOMER’S USE OF THE SERVICES DURING
                      THE FREE TRIAL PERIOD, ANY BREACH BY CUSTOMER OF THIS
                      AGREEMENT, AND ANY OF CUSTOMER’S INDEMNIFICATION
                      OBLIGATIONS HEREUNDER.
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      You acknowledge and agree that any termination of access
                      to the Free Services may result in the loss of features or
                      capacity of the Free Services. Mindhouse may modify or
                      discontinue the provision of Free Services (or any part
                      thereof) at any time without notice, and that Mindhouse
                      shall not be liable to You or any third party for such
                      modification or discontinuation. Mindhouse may, in its
                      sole discretion, elect to provide You with an opportunity
                      to retrieve data from the Free Services following any
                      termination of the Free Services.
                    </Typography>
                  </LI>
                </OL>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Payment Terms.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Fee.</Strong> Customer will pay all fees as specified
                  by Mindhouse on its Platform. Except as otherwise agreed by
                  and between parties, (i) fees are based on Services availed,
                  and (ii) payment obligations are non-cancelable and fees are
                  non-refundable.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Change in Fee.</Strong> Mindhouse may change the fees
                  charged for the Services at any time and from time to time.
                  The changes will become effective only at the end of the
                  then-current billing cycle of the applicable Purchased
                  Services. Mindhouse will provide You with reasonable prior
                  written notice of any change in fees to give You an
                  opportunity to cancel any on-going subscriptions before the
                  price change becomes effective.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Promotional Prices and Discounts:</Strong> Promotional
                  prices, including but not limited to reduced prices,
                  promo-codes, vouchers, and discount coupons, are for one-time
                  use only. Promotional prices will not be applicable at the
                  time of renewal of Your subscriptions, or subsequent payments
                  or purchases. Promotional prices cannot be combined, i.e. only
                  one of the reduced price, promo-code, voucher, or discount
                  coupon, can be used for a single transaction.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Taxes.</Strong> Mindhouse’s fees do not include any
                  taxes, levies, duties, or similar governmental assessments of
                  any nature, including, for example, value-added, sales, goods
                  and service, use or withholding taxes, assessable by any
                  jurisdiction whatsoever (collectively,{" "}
                  <Strong>“Taxes”</Strong>). Customer is responsible for paying
                  all Taxes associated with its purchases hereunder. If
                  Mindhouse has the legal obligation to pay or collect Taxes for
                  which Customer is responsible under this Section 7, Mindhouse
                  will invoice Customer and Customer will pay that amount unless
                  Customer provides Mindhouse with a valid tax exemption
                  certificate authorized by the appropriate taxing authority.
                  For clarity, Mindhouse is solely responsible for taxes
                  assessable against it based on its income, property, and
                  employees.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Proprietary Rights and Licenses.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Reservation of Rights.</Strong> Subject to the limited
                  rights expressly granted hereunder, Mindhouse, its Affiliates,
                  its licensors and content providers reserve all of their
                  rights, titles, and interests in and to the Services, logos,
                  software, Platform Content, and other materials created by
                  them, including all of their related intellectual property
                  rights. Mindhouse owns all rights, titles, and interests in
                  and to Mindhouse’s registered and unregistered, domestic and
                  foreign trademarks, service marks, trademark applications,
                  service mark applications, trade names, patents, patent
                  applications, copyrights, copyright applications, discoveries,
                  know-how and trade secrets of and relating to the Platform and
                  Services. You agree not to remove and to always include any
                  and all copyright and intellectual property notices attached
                  to intellectual property owned by or otherwise licensed to You
                  by Mindhouse, its Affiliates, its licensors and/or its content
                  providers, including but not limited to all templates and
                  learning materials. No one may copy, reproduce, transmit,
                  post, distribute or create, derivative works from the Platform
                  or Services without express, prior written authorization from
                  Mindhouse. No rights are granted to You hereunder other than
                  as expressly set forth herein.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>License by Customer to Mindhouse.</Strong> You hereby
                  grant to Mindhouse, and its Affiliates a worldwide,
                  royalty-free, non-exclusive, limited license to host, copy,
                  use, transmit, display and distribute any intellectual
                  property as necessary to perform the obligations under this
                  Agreement. In addition to the foregoing, Customer hereby
                  grants to Mindhouse a non-exclusive, worldwide, irrevocable,
                  royalty-free right to use Customer’s name, logo, mark and/or
                  testimonials in marketing materials, including but not limited
                  to the Platform.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>License by Customer and User to Use Feedback.</Strong>{" "}
                  Mindhouse may occasionally request feedback from You regarding
                  Your use of the Service. You grant to Mindhouse, its
                  Affiliates, assignees, subsidiaries, and successors a
                  worldwide, perpetual, irrevocable, and royalty-free license to
                  use and incorporate into its products and services any
                  suggestion, enhancement request, recommendation, correction or
                  other feedback provided by You relating to the operation of
                  Mindhouse’s or its Affiliates’ services without any
                  obligations to You, including but not limited to
                  acknowledgement, limitation, or notice of any kind.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Confidentiality and Non-Disclosure.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Protection of Confidential Information.</Strong> As
                  between the parties, each party retains all ownership rights
                  in and to its Confidential Information. The Receiving Party
                  will use the same degree of care that it uses to protect the
                  confidentiality of its own confidential information of like
                  kind, but in no event less than a reasonable degree of care
                  to: (i) not use any Confidential Information of the Disclosing
                  Party for any purpose outside the scope of this Agreement and
                  (ii) except as otherwise authorized by the Disclosing Party in
                  writing, limit access to Confidential Information of the
                  Disclosing Party to those of its and its Affiliates’ employees
                  and contractors who need that access for purposes consistent
                  with this Agreement and who have confidentiality obligations
                  not materially less protective of the Confidential Information
                  than those herein. Notwithstanding anything to the contrary
                  above, Mindhouse may disclose: (a) the terms of this Agreement
                  to a subcontractor or independent third-party service
                  provider(s) to perform Mindhouse’s obligations under this
                  Agreement, under terms of confidentiality materially as
                  protective as set forth herein; and/or (b) as required or
                  permitted by Section 8.2 above and/or Section 8.3 above.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Compelled Disclosure.</Strong> The Receiving Party may
                  disclose Confidential Information of the Disclosing Party to
                  the extent compelled by law, law enforcement, or a court
                  order, to do so, provided that the Receiving Party gives: (a)
                  prior notice of the compelled disclosure to the Disclosing
                  Party (to the extent legally permitted); and (b) reasonable
                  assistance, at the Disclosing Party’s cost, if the Disclosing
                  Party wishes to contest the disclosure. Mindhouse may disclose
                  Confidential Information of the Customer to the extent
                  compelled by law, law enforcement, or a court order, to do so.
                  If the Receiving Party is compelled by law or a legal
                  proceeding to disclose the Disclosing Party’s Confidential
                  Information as part of a civil proceeding to which the
                  Disclosing Party is a party, and the Disclosing Party is not
                  contesting the disclosure, the Disclosing Party will reimburse
                  the Receiving Party for its reasonable cost of compiling and
                  providing secure access to that Confidential Information.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Additional terms for mobile application
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>E-License to the App.</Strong> Mindhouse may make
                  available software to access the Service via an App. To use
                  the App, You must have a mobile device that is compatible with
                  the App. Mindhouse does not warrant that the App will be
                  compatible with Your mobile device. You may use mobile data in
                  connection with the App and may incur additional charges from
                  Your wireless provider for these services. You agree that You
                  are solely responsible for any charges. Subject to the terms
                  of this Agreement, Mindhouse hereby grants You a
                  non-exclusive, non-transferable, non-sublicensable, and
                  revocable license to use a complied code copy of the App for
                  Your personal use. You may not: (i) modify, disassemble,
                  decompile or reverse engineer the App, except to the extent
                  that such restriction is expressly prohibited by law; (ii)
                  rent, lease, loan, resell, sub-license, distribute or
                  otherwise transfer the App to any third party or use the App
                  to provide time sharing or similar services for any third
                  party; (iii) make any copies of the App; (iv) remove,
                  circumvent, disable, damage or otherwise interfere with
                  security-related features of the App, features that prevent or
                  restrict use of copying of any content accessible through the
                  App, or features that enforce limitation on use of the App;
                  and/or (v) delete the copyright and other proprietary rights
                  notice on the App. You acknowledge that Mindhouse may from
                  time to time issue upgraded versions of the App and may
                  automatically electronically upgrade the version of the App
                  that You are using on Your mobile device. You consent to such
                  automatic upgrading on Your mobile device and agree that the
                  terms and conditions of this Agreement will apply to such
                  upgrades. Any third-party code that may be incorporated in the
                  App is covered by the applicable open-source or third-party
                  license end user license agreement, if any, authorizing use of
                  such code. The foregoing license grant is not a sale of the
                  App or any copy thereof, and Mindhouse or its third-party
                  partners or suppliers retain all rights, titles, and interest
                  in the App (and any copy thereof). Any attempt by You to
                  transfer any of the rights, duties, or obligations hereunder,
                  except as expressly provided for in this Agreement, is void.
                  Mindhouse reserves all rights not expressly granted under this
                  Agreement.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Additional Terms for App from Apple Store.</Strong>{" "}
                  The following applies to any App You acquire from the Apple
                  App Store (<Strong>“Apple-Sourced Software”</Strong>): You
                  acknowledge and agree that this Agreement is solely between
                  You and Mindhouse, not Apple, Inc. (<Strong>“Apple”</Strong>)
                  and that Apple has no responsibility for the Apple-Sourced
                  Software or content thereof. Your use of the Apple-Sourced
                  Software must comply with the App Store Terms of Service. You
                  acknowledge that Apple has no obligation whatsoever to furnish
                  any maintenance and support services with respect to the
                  Apple-Sourced Software. In the event of any failure of the
                  Apple-Sourced Software to conform to any applicable warranty,
                  You may notify Apple, and Apple will refund the purchase price
                  for the Apple-Sourced Software to You; to the maximum extent
                  permitted by applicable law, Apple will have no other warranty
                  obligation whatsoever with respect to the Apple-Sourced
                  Software, and any other claims, losses, liabilities, damages,
                  costs or expenses attributable to any failure to conform to
                  any warranty will be solely governed by these Terms and any
                  law applicable to Mindhouse as provider of the software. You
                  acknowledge that Apple is not responsible for addressing any
                  claims of You or any third party relating to the Apple-Sourced
                  Software or Your possession and/or use of the Apple-Sourced
                  Software, including, but not limited to: (a) product liability
                  claims; (b) any claim that the Apple-Sourced Software fails to
                  conform to any applicable legal or regulatory requirement; and
                  (c) claims arising under consumer protection or similar
                  legislation; and all such claims are governed solely by these
                  Terms and any law applicable to Mindhouse as provider of the
                  software. You acknowledge that, in the event of any
                  third-party claim that the Apple-Sourced Software or your
                  possession and use of that Apple-Sourced Software infringes
                  that third party’s intellectual property rights, Mindhouse,
                  not Apple, will be solely responsible for the investigation,
                  defense, settlement and discharge of any such intellectual
                  property infringement claim to the extent required by these
                  Terms. You and Mindhouse acknowledge and agree that Apple, and
                  Apple’s subsidiaries, are third party beneficiaries of these
                  Terms as relates to Your license of the Apple-Sourced
                  Software, and that, upon Your acceptance of the terms and
                  conditions of these Terms, Apple will have the right (and will
                  be deemed to have accepted the right) to enforce these Terms
                  as relates to Your license of the Apple Sourced Software
                  against You as a third-party beneficiary thereof.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Mobile Applications from Google Play Store.</Strong>{" "}
                  The following applies to any App You acquire from the Google
                  Play Store (<Strong>“Google Sourced Software”</Strong>): (i)
                  You acknowledge that these Terms are between You and Mindhouse
                  only, and not with Google, Inc. (<Strong>“Google”</Strong>);
                  (ii) Your use of Google-Sourced Software must comply with
                  Google’s then-current Google Play Store Terms of Service;
                  (iii) Google is only a provider of the Google Play Store where
                  You obtained the Google-Sourced Software; (iv) Mindhouse, and
                  not Google, is solely responsible for its Google-Sourced
                  Software; (v) Google has no obligation or liability to you
                  with respect to Google Sourced Software or these Terms; and
                  (vi) You acknowledge and agree that Google is a third-party
                  beneficiary to these Terms as it relates to Mindhouse’s
                  Google-Sourced Software.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Representations and Warranty Disclaimers.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Representations.</Strong> Each party represents that
                  it has (i) no legal or contractual obligations that prevent it
                  from entering into this Agreement; and (ii) the legal power to
                  do so. Each individual executing this Agreement on behalf of a
                  party hereby represents and warrants that it is authorized to
                  do so on behalf of such party.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Mindhouse Warranties.</Strong> Mindhouse warrants that
                  during an applicable subscription term (a) this Agreement will
                  accurately describe the applicable administrative, physical,
                  and technical safeguards for protection of the security,
                  confidentiality and integrity of Customer’s data, (b)
                  Mindhouse will not materially decrease the overall security of
                  the Services, (c) the Services will be provided in a
                  workmanlike manner that conforms to the relevant, prevailing
                  industry standard, and (d) Mindhouse will not decrease the
                  overall functionality of the Services. For any breach of the
                  warranty above, Customer’s exclusive remedies are those
                  described in Section 14.3 and 14.4 below.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Disclaimer of Warranties.</Strong> EXCEPT AS EXPRESSLY
                  PROVIDED HEREIN, MINDHOUSE DOES NOT MAKE ANY WARRANTY OF ANY
                  KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, AND
                  MINDHOUSE SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES,
                  INCLUDING ANY IMPLIED WARRANTY OF MECHANTABILITY, FITNESS FOR
                  A PARTICULAR PURPOSE, QUIET ENJOTMENT, DELAY IN SERVICES,
                  RESULTS OBTAINED FROM USING THE PLATFORM, ERRORS OR DEFFECTS
                  IN THE PLATFORM OR SERVICES, OR NON-INFRINGEMENT TO THE
                  MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW. FREE SERVICES ARE
                  PROVIDED “AS IS”, AND AS AVAILABLE EXCLUSIVE OF ANY WARRANTY
                  WHATSOEVER. MINDHOUSE EXPRESSLY DISCLAIMS THAT SERVICES WILL
                  BE UNINTERRUPTED, TIMELY, SECURE, VIRUS-FREE, ERROR-FREE,
                  ACCURATE, OR RELIABLE. ANY CONTENT DOWNLOADED THROUGH THE
                  PLATFORM, IS DONE AT YOUR OWN RISK AND YOU SHALL REMAIN SOLELY
                  RESPONSIBLE FOR ANY DAMAGE CAUSED BY SUCH CONTENT, INCLUDING
                  ANY LOSS OF DATA OR DAMAGE CAUSED TO YOUR MOBILE DEVICE.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Medical Disclaimer.</Strong> MINDHOUSE DOES NOT MAKE
                  ANY KIND OF CLAIM, REPRESENTATION, OR GUARANTEE THAT THE
                  SERVICES OFFERED WILL PROVIDE A THERAPUTIC BENEFIT TO YOU.
                  MINDHOUSE IS NOT A HEALTH CARE PROVIDER OR A MEDICAL SERVICE
                  PROVIDER. THE SERVICES PROVIDED ARE NOT A REPLACEMENT OF ANY
                  FORM OF MEDICAL ADVICE OR THERAPY. THE SERVICES PROVIDED ARE
                  NOT INTENTED TO CURE, TREAT, OR DIAGNOSE ANY MEDICAL
                  CONDITIONS WHICH YOU MAY HAVE.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Indemnification.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Indemnity by Mindhouse.</Strong> Mindhouse will defend
                  at its expense any cause of action brought against You, to the
                  extent that such cause of action is based on a claim that the
                  Service, as hosted by Mindhouse for Customer, infringes a
                  patent, copyright, or trade secret of a third party. Mindhouse
                  will pay those costs and damages finally awarded against
                  Customer pursuant to any such claim or paid in settlement of
                  any such claim if such settlement was approved in advance by
                  Mindhouse. The Customer may retain its own counsel at the
                  Customer’s own expense.‍
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>No Liability.</Strong> Mindhouse will have no
                  liability for any claim of infringement based on: (i) Services
                  which have been modified by parties other than Mindhouse where
                  the infringement claim would not have occurred in the absence
                  of such modification; (ii) Your use of the Service in
                  conjunction with data or third-party software where use with
                  such data or third-party software gave rise to the
                  infringement claim; (iii) Your use of the Service outside the
                  permitted scope of the Agreement; (iv) use of Service(s)
                  provided for no fee including but not limited to Trial
                  Service; and/or (v) breach of Agreement by You.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Remedies.</Strong> In the event a claim is made or
                  likely to be made, Mindhouse may (i) procure for Customer the
                  right to continue using the Service under the terms of the
                  Agreement, or (ii) replace or modify the Service to be
                  non-infringing without a material decrease in functionality.
                  If these options are not commercially reasonably available,
                  Mindhouse or Customer may terminate Customer’s subscription to
                  the affected Service upon written notice to the other party.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Indemnity by Customer.</Strong> Customer will defend,
                  indemnify, and hold Mindhouse and its officers, directors,
                  employees, consultants, sub-contractors and/or agents harmless
                  from and against any and all damages, costs, liabilities,
                  expenses (including, without limitation, reasonable attorneys’
                  fees), and settlement amounts incurred in connection with any
                  claim arising from or relating to Customer’s: (i) breach of
                  any of its obligations set forth in this Agreement; (ii)
                  actual or alleged use of the Services in violation of this
                  Agreement or Applicable Law by Customer or User; or (iii) any
                  actual or alleged infringement or misappropriation of third
                  party intellectual property rights arising from data provided
                  to Mindhouse by the Customer or otherwise added into the
                  Service by the Customer or User.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Indemnification Procedures.</Strong> Each indemnifying
                  party’s obligations as set forth in this Section 12 are
                  subject to the other party: (i) giving the indemnifying party
                  prompt written notice of any such claim or the possibility
                  thereof; (ii) giving the indemnifying party sole control over
                  the defense and settlement of any such claim; and (iii)
                  providing full cooperation in good faith in the defense of any
                  such claim. Any settlement of a claim will not include a
                  financial or specific performance obligation on, or admission
                  of liability by, the party against whom the claim is brought.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Exclusive Remedy.</Strong> This Section 12 states the
                  indemnifying party’s sole liability to, and the indemnified
                  party’s exclusive remedy against, the other party for any
                  third-party claim described in this Section 12.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Limitation of Liability.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Limitation of Liability.</Strong> IN NO EVENT SHALL
                  THE AGGREGATE LIABILITY OF EACH PARTY TOGETHER WITH ALL OF ITS
                  AFFILIATES ARISING OUT OF OR RELATED TO THIS AGREEMENT EXCEED
                  THE TOTAL AMOUNT PAID BY CUSTOMER HEREUNDER FOR THE SERVICES
                  GIVING RISE TO THE LIABILITY IN THE TWELVE MONTHS PRECEDING
                  THE FIRST INCIDENT OUT OF WHICH THE LIABILITY AROSE. THE
                  FOREGOING LIMITATION WILL APPLY WHETHER AN ACTION IS IN
                  CONTRACT OR TORT AND REGARDLESS OF THE THEORY OF LIABILITY BUT
                  WILL NOT LIMIT CUSTOMER'S PAYMENT OBLIGATIONS UNDER SECTION 7
                  ABOVE. MINDHOUSE’S LIABILITY FOR DAMAGES UNDER THIS AGREEMENT
                  IS LIMITED TO DIRECT MONETARY DAMAGES. IN NO EVENT WILL
                  MINDHOUSE BE LIABLE FOR PROVIDING SUBSTITUTE SERVICES.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>
                    Exclusion of Consequential and Related Damages.
                  </Strong>{" "}
                  IN NO EVENT SHALL MINDHOUSE, ITS AFFILIATES, OFFICERS,
                  DIRECTORS, EMPLOYEES, AGENTS, VENDORS, OR SUPPLIERS BE LIABLE
                  UNDER CONTRACT, TORT, STRICT LIABILITY, NEGLIGENCE, OR ANY
                  OTHER LEGAL THEORY WITH RESPECT TO THE SERVICES (OR ANY
                  CONTENT OR INFORMATION AVAILABLE THROUGH THE SERVICES): (1)
                  FOR ANY LOST PROFITS, REVENUES, DATA, GOODWILL, OR INDIRECT,
                  SPECIAL, INCIDENTAL, CONSEQUENTIAL, COVER, BUSINESS
                  INTERRUPTION OR PUNITIVE DAMAGES, (2) FOR THE USE OR INABILITY
                  TO USE THE SERVICE, (3) FOR ANY INACCURACY, ERROR OR OMISSION
                  IN SERVICES, OR (4) FOR ANY BUGS, VIRUSES, TROJAN HORSES, OR
                  THE LIKE (REGARDLESS OF THE SOURCE OF ORIGINATION), EVEN IF
                  MINDHOUSE OR ITS AFFILIATES HAVE BEEN ADVISED OF THE
                  POSSIBILITY OF THE ABOVE OR IF MINDHOUSE’S OR ITS AFFILIATES’
                  REMEDY OTHERWISE FAILS IN ITS ESSENTIAL PURPOSE. THE FOREGOING
                  LIMITATIONS SHALL NOT APPLY TO THE EXTENT PROHIBITED BY
                  APPLICABLE LAW.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Essential Basis.</Strong> CUSTOMER ACKNOWLEDGES THAT
                  MINDHOUSE HAS SET ITS PRICES AND ENTERED INTO THIS AGREEMENT
                  IN RELIANCE ON THE DISCLAIMERS OF WARRANTIES AND LIMITATION OF
                  LIABILITY AND THE SAME FORM AN ESSENTIAL BASIS FOR THE BARGAIN
                  BETWEEN THE PARTIES.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Terms and Termination.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Term of the Agreement.</Strong> This Agreement
                  commences on the date Your first accept it and continues until
                  the subscription has been terminated by either party under the
                  terms of this Agreement. Services purchased renew
                  automatically until the subscription is cancelled by the
                  Customer or this Agreement is terminated by either party.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Termination by Us.</Strong> Mindhouse may terminate
                  this Agreement, Purchased Service(s), and/ or Free Services
                  under this Agreement for a cause (a) upon 30 days written
                  notice to Customer of a breach of this Agreement by Customer
                  if such breach remains uncured at the expiration of such
                  period, or (b) immediately upon sending a written notice if
                  Customer becomes the subject of a petition in bankruptcy or
                  any other proceeding relating to insolvency, receivership,
                  liquidation, assignment for the benefit of creditors, or (c)
                  immediately upon breach of obligations under Section 9 above,
                  or (d) immediately upon breach of obligations under Section 8
                  above, or (e) upon 30 days written notice to Customer without
                  assigning any reason.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Termination by Customer.</Strong> Customer may
                  terminate this Agreement for cause (a) upon 30 days’ written
                  notice to Mindhouse of a material breach of this Agreement by
                  Mindhouse if such breach remains uncured at the expiration of
                  such period, or (b) immediately upon sending a written notice
                  if Mindhouse becomes the subject of a petition in bankruptcy
                  or any other proceeding relating to insolvency, receivership,
                  liquidation, or assignment for the benefit of creditors.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Refund or Payment Upon Termination.</Strong> If this
                  Agreement is terminated by Customer or Mindhouse in accordance
                  under Section 14.2 or 14.3 above, then Mindhouse shall refund
                  to Customer any prepaid fees covering the remainder of the
                  term after the effective date of termination on a pro-rated
                  basis. In no circumstances shall Mindhouse be liable to refund
                  any amount to Customer for termination of this Agreement or
                  any Service by Customer except if such termination has been
                  done by Customer or Mindhouse under Section 14.2 or 14.3
                  above. Termination of this Agreement does not relieve Customer
                  of its obligation to pay for Services provided by Mindhouse
                  prior to the effective date of such termination.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Surviving Provisions.</Strong> Sections, clauses and
                  provisions of this Agreement that by their nature are intended
                  to survive termination or expiration, will survive any
                  termination or expiration of this Agreement for a period of
                  three (3) years, except as otherwise provided in this
                  Agreement or required by Applicable Law, including but not
                  limited to the following Sections: 2.2, 4.4.1, 5, 6.3.2, 7, 8,
                  9, 11, 12, 13, 14.4, 14.5, 15, and 16.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Dispute resolution
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Good Faith Consultation.</Strong> The parties agree
                  that they will attempt to resolve through good faith
                  consultation, any dispute or difference in respect of,
                  concerning, or connected with, the interpretation of this
                  Agreement or performance of obligations under this Agreement,
                  or otherwise arising out of this Agreement. The parties agree
                  that such good faith consultation will begin promptly after
                  either party has delivered a written request for such
                  consultation to the other party.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Arbitration</Strong>. In the event that the parties
                  are not able to resolve a dispute within thirty (30) days in
                  accordance with the mechanism provided above, the dispute
                  shall be settled by a single arbitrator appointed by mutual
                  consent of both parties. In the event that the parties to this
                  Agreement fail to appoint an arbitrator within fifteen (15)
                  days from the date of expiry of the mechanism specified above,
                  an arbitrator may be appointed by a court of competent
                  jurisdiction. The venue and seat of arbitration shall be in
                  Delhi, India, only. The decision of the arbitrator shall be
                  binding upon the parties, and can be enforced in a court of
                  competent jurisdiction. The clause constitutes a binding
                  consent by the parties for any proceedings in terms hereof and
                  no party shall be entitled to withdraw from the proceedings.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading
              mb={"5px !important"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              General Provisions.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Modifications</Strong>
                </Typography>
                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Modification of this Agreement.</Strong> We
                      reserve all rights to modify the Agreement at Our sole
                      discretion from time to time. You shall be responsible for
                      reviewing this Agreement from time to time for
                      modifications. The last date of modification of this
                      Agreement will be displayed on this page. We may, for Our
                      convenience only and without waiving Your obligation to
                      periodically review the Agreement, attempt to notify You
                      of significant modifications to this Agreement through the
                      Platform, in an email notification, or through reasonable
                      means. The modification will be in effect after the
                      completion of fourteen (14) days from the date when the
                      notification was posted or sent, except that the following
                      categories of modification may be made effective
                      immediately: (a) modifications required to provide new
                      features without negatively affecting Your rights under
                      this Agreement; (b) modifications made to correct
                      typographical errors; and/or (c) modifications made to
                      comply with legal obligations, including but not limited
                      to Our or Your obligations under the Applicable Law. You
                      understand and agree that Your registration on, or
                      continued access or use of, the Platform beyond the
                      effective date of such modifications shall be deemed to be
                      Your acceptance of all modifications.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Modification of Platform.</Strong> We may, from
                      time to time, at Our sole discretion and without any
                      notification, add new features, remove or stop offering
                      old features, or modify existing features of the Platform.
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Assignment.</Strong> Neither party may assign any of
                  its rights or obligations hereunder, whether by operation of
                  law or otherwise, without the other party’s prior written
                  consent (not to be unreasonably withheld); provided, however,
                  either party may assign this Agreement in its entirety,
                  without the other party’s consent to its Affiliate or in
                  connection with a merger, acquisition, corporate
                  reorganization, or sale of all or substantially all of its
                  assets. Notwithstanding the foregoing, if a party is acquired
                  by, sells substantially all of its assets to, or undergoes a
                  change of control in favor of, a direct competitor of the
                  other party, then such other party may terminate this
                  Agreement upon written notice. In the event of such
                  termination, Mindhouse will refund Customer any prepaid fees
                  covering the remainder of the term of all subscriptions for
                  the period after the effective date of such termination.
                  Subject to the foregoing, this Agreement will bind and inure
                  to the benefit of the parties, their respective successors,
                  and permitted assigns.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Force Majeure.</Strong> Upon the occurrence of a Force
                  Majeure Event, the parties agree to excuse performance under
                  this Agreement and not hold the other liable for the delay in
                  or failure of performance under this Agreement. Any such delay
                  in or failure of performance shall not constitute a default or
                  give rise to any liability for damages.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Entire Agreement.</Strong> This Agreement is the
                  entire agreement between Mindhouse and Customer regarding
                  Customer’s use of Services and supersedes all prior and
                  contemporaneous agreements, proposals, or representations,
                  written or oral, concerning its subject matter.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Notices.</Strong> Notices by Mindhouse under this
                  Agreement may be shown in the user interface of the Platform,
                  or sent to the contact information provided by You. Notices by
                  You to Mindhouse may be sent to the contact information
                  provided in Section 15.11 below.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Waiver.</Strong> No failure or delay by either party
                  in exercising any right under this Agreement will constitute a
                  waiver of that right unless otherwise agreed between the
                  parties in writing
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Severability.</Strong> If any provision of this
                  Agreement is held by a court of competent jurisdiction to be
                  contrary to law, the provision will be deemed null and void,
                  and the remaining provisions of this Agreement will remain in
                  effect.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Third Party Beneficiaries.</Strong> There are no
                  third-party beneficiaries under this Agreement.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Relationship of Parties.</Strong> The parties are
                  independent contractors. This Agreement does not create a
                  partnership, franchise, joint venture, agency, fiduciary or
                  employment relationship between the parties. Each party will
                  be solely responsible for payment of all compensation owed to
                  its employees, as well as all employment-related taxes.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Governing Law and Jurisdiction.</Strong> The Agreement
                  shall be governed by and construed in accordance with the laws
                  of India, without giving effect to any principles of conflict
                  of law. The courts of Delhi, India shall have sole and
                  exclusive jurisdiction for all disputes arising out of the
                  Agreement. (.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Contact Us.</Strong> For any questions or concerns
                  related to the Agreement, please contact Us at{" "}
                  <Typography
                    variant="link1"
                    link1Color={theme.colorsV2.blue.shade_700}
                    fontFamily={"var(--man-400)"}
                    href="mailto:help@betheshyft.com"
                  >
                    help@betheshyft.com.
                  </Typography>
                </Typography>
              </LI>
            </OL>
          </LI>
        </OL>
      </Wrapper>
      <GlobalCss />
    </Container>
  );
};
function TermsConditions() {
  useDisableIntercom();
  return <TermsPageContent />;
}

export default TermsConditions;
const Heading = styled(DefaultTypography)`
  text-decoration: underline;
  font-family: var(--man-400);
  font-weight: 600;
`;
const Typography = styled(DefaultTypography)`
  text-align: justify;
`;
const Container = styled(FlexContainer)`
  margin: 40px auto auto;
`;
const AlphaList = styled.li`
  list-style-type: ${({ capital }) =>
    capital ? "upper-alpha" : "lower-alpha"};
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const RomanList = styled.li`
  list-style-type: lower-roman;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const Wrapper = styled(FlexDiv)`
  padding: ${(props) => {
    return props.showHeightGap ? "110px 0px" : "10px 0px";
  }};
  p {
    margin-bottom: 11px;
    &:last-child {
      margin-bottom: 0px;
    }
  }
`;
const OL = styled.ol`
  ${space}
  ol {
    padding-inline-start: 40px !important;
  }
  counter-reset: item;
  > li {
    padding-left: 0px;
    li {
      padding-left: 0px;
    }
    ${AlphaList} {
      padding-left: 0;
    }
    ${Heading} {
      margin-bottom: 25px;
    }
  }
`;
const LI = styled.li`
  ${space}
  display: block;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
  position: relative;
  &:before {
    display: inline-block;
    float: left;
    margin-right: 5px;
    content: counters(item, ".") ".";
    counter-increment: item;
    font-size: 12px;
    font-family: var(--man-400);
    color: ${theme.colors.gray_100};
    @media only screen and (min-width: 640px) {
      font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
      font-size: 16px;
    }
  }
`;
const Strong = styled.strong`
  font-size: inherit;
  font-family: var(--font-700);
  color: inherit;
`;
const SUBOL = styled(OL)`
  counter-reset: item;
`;
const Link = styled(ALink)`
  font-size: inherit;
  font-family: inherit;
  color: ${theme.colors.dotsColor};
  ${({ isNormal }) =>
    !isNormal &&
    `
    font-style: italic;
  `};
  &:hover {
    color: ${theme.colors.dotsColor};
  }
`;
const ItalicText = styled.i`
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  font-style: italic;
`;
const GlobalCss = createGlobalStyle`
::marker {
    font-size: 12px;
    color: ${theme.colors.gray_100};
    font-family: 'Raleway',sans-serif;
    @media only screen and (min-width: 640px) {
    font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
    font-size: 16px;
    }

    
}
  `;
